import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {PropulsionPortfolioLoad} from '../models/propulsion-portfolio-load.model';
import {PropulsionSnapshotViolation} from '../models/propulsion-snapshot-violation.model';
import {PropulsionSnapshot} from '../models/propulsion-snapshot.model';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})
export class PropulsionSnapshotService {

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  /**
   * Get the existing propulsion snapshot data
   */
  getPropulsionSnapshot(): Observable<PropulsionSnapshot[]> {
    return this.http.get<any>(`${this.configService.baseApiUrl}portfolio/propulsion-snapshot`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get the Propulsion snapshot data that is not in the Vehicle Snapshot data
   */
  getUnmatchedPropulsionSnapshot(): Observable<PropulsionSnapshot[]> {
    return this.http.get<any>(`${this.configService.baseApiUrl}portfolio/propulsion-snapshot/unmatched-propulsion-snapshot`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Transforms the GM3PD Propulsion snapshot the user uploads to an object to represent the data
   * @param file the GM3PD Propulsion snapshot file that is uploaded
   * @param sheetList a list containing the selected sheets to read data from
   */
  transform(file: File, sheetList: string[]): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheets', sheetList.toString());
    return this.http.post<any>(
      `${this.configService.baseApiUrl}portfolio/propulsion-snapshot/transform`, formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Validate the GM3PD Propulsion Snapshot load
   * @param propSnapshot The GM3PD propulsion snapshot
   * @return Observable<Map<string, PropulsionSnapshotViolation>> - returns mapping of lists of violations
   */
  validate(propSnapshot: Map<string, PropulsionSnapshot[]>): Observable<Map<string, PropulsionSnapshotViolation>> {
    const url = `${this.configService.baseApiUrl}portfolio/propulsion-snapshot/validate`;
    return this.http.post<Map<string, PropulsionSnapshotViolation>>(
      url, propSnapshot).pipe(catchError(error => this.handleError(error))
    );
  }

  /**
   * Load the GM3PD Propulsion snapshot
   * @param propSnapshot The GM3PD propulsion snapshot
   * @return Observable<Map<string, PropulsionSnapshot[]>> - returns the snapshot data that was loaded
   */
  load(propSnapshot: Map<string, PropulsionSnapshot[]>): Observable<Map<string, PropulsionSnapshot[]>> {
    const url = `${this.configService.baseApiUrl}portfolio/propulsion-snapshot/load`;
    return this.http.post<Map<string, PropulsionSnapshot[]>>(url, propSnapshot).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Process a Propulsion snapshot that is currently loaded into the system
   *
   * @param portType the portfolio type (Business Plan or TVP)
   * @param gmid the user id that is executing the process snapshot
   */
  processSnapshot(portType: string, gmid: string): Observable<Map<string, PropulsionPortfolioLoad[]>> {
    const url = `${this.configService.baseApiUrl}portfolio/propulsion-snapshot/process`;
    return this.http.post<any>(url, {portType, gmid}).pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   */
  private handleError(errorResponse: HttpErrorResponse): Observable<any> {
    let errorMessage = 'An unexpected error has occurred.';
    if (errorResponse.error && errorResponse.error.message) {
      errorMessage = errorResponse.error.message;
    }
    return throwError(errorMessage);
  }
}
